import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/contexts/ToastContext';
import { useProfiler } from '@/hooks/useProfiler';
import type { 
  Order, 
  OrderItem, 
  MenuItem, 
  Payment, 
  PaymentType,
  SplitPaymentDetails, 
  OrderStatus
} from '@/types/common';
import { formatCurrency } from '@/utils/format';
import RegularPayment from '@/components/Payment/RegularPayment';
import SplitPayment from '@/components/Payment/SplitPayment';
import { Profiler } from 'react';

interface OrderItemWithDetails extends OrderItem {
  menu_item: MenuItem;
}

interface OrderWithItems extends Order {
  items: OrderItemWithDetails[];
  paid_amount: number;
}

type PaymentMode = 'regular' | 'split';
type PaymentStatus = 'idle' | 'processing' | 'completed' | 'error';

export default function PaymentPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const onRender = useProfiler('PaymentPage');

  const [order, setOrder] = useState<OrderWithItems | null>(null);
  const [paymentType, setPaymentType] = useState<PaymentType>('cash');
  const [paymentMode, setPaymentMode] = useState<PaymentMode>('regular');
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>('idle');
  const [loading, setLoading] = useState(true);
  const [cashReceived, setCashReceived] = useState<number>(0);
  const [customAmount, setCustomAmount] = useState<number | null>(null);
  const [remainingAmount, setRemainingAmount] = useState<number>(0);

  // Fetch order details
  useEffect(() => {
    const fetchOrder = async () => {
      if (!orderId) return;
      
      try {
        const { data, error } = await supabase
          .from('orders')
          .select(`
            *,
            items:order_items(
              *,
              menu_item:menu_items(*)
            )
          `)
          .eq('id', orderId)
          .single();

        if (error) throw error;
        
        // Check if order is already paid
        if (data.status === 'paid') {
          addToast({
            type: 'info',
            message: 'This order has already been paid',
          });
          navigate('/orders');
          return;
        }

        setOrder({ ...(data as Order), paid_amount: 0 } as OrderWithItems);
      } catch (error) {
        console.error('Error fetching order:', error);
        addToast({
          type: 'error',
          message: 'Failed to load order details',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId, addToast, navigate]);

  // Set initial cash received when component mounts or payment details change
  useEffect(() => {
    const amountToPay = (customAmount ?? order?.total) || 0;
    setCashReceived(amountToPay);
    setRemainingAmount((order?.total || 0) - (order?.paid_amount || 0));
  }, [order?.total, customAmount, order?.paid_amount]);

  const handlePaymentSubmit = async (paymentDetails: {
    payment_type: PaymentType;
    amount: number;
    tip_amount: number;
    custom_amount: number;
    received?: number;
  }) => {
    setPaymentStatus('processing');

    try {
      // First, get the current order status
      const { data: currentOrder, error: fetchError } = await supabase
        .from('orders')
        .select('status')
        .eq('id', orderId)
        .single();

      if (fetchError) throw fetchError;

      // Prepare payment data based on payment type
      const paymentData = {
        order_id: order?.id as string,
        amount: paymentDetails.amount,
        payment_type: paymentDetails.payment_type,
        tip_amount: paymentDetails.tip_amount || 0,
        status: 'completed',
        // For cash payments, custom_amount should be the actual custom amount
        custom_amount: paymentDetails.custom_amount || 0
      };

      const { error: paymentError } = await supabase
        .from('payments')
        .insert(paymentData);

      if (paymentError) throw paymentError;

      // Update local order state
      setOrder(prevOrder => prevOrder ? {
        ...prevOrder,
        status: 'paid',
        updated_at: new Date().toISOString()
      } : null);

      setPaymentStatus('completed');
      addToast({
        type: 'success',
        message: `Payment of ${formatCurrency(paymentDetails.amount)} processed successfully`
      });

      setTimeout(() => {
        navigate('/orders');
      }, 1500);
    } catch (error) {
      setPaymentStatus('error');
      addToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to process payment'
      });
    }
  };

  // Process split payment
  const handleSplitPayment = async (splits: SplitPaymentDetails[]) => {
    if (!order) return;

    try {
      setPaymentStatus('processing');

      // Calculate total amount being paid including tips and custom amounts
      const totalPaid = splits.reduce((sum, split) => {
        const baseAmount = split.items.reduce((itemSum, item) => itemSum + item.amount, 0);
        return sum + baseAmount;
      }, 0);

      // Calculate total tips and custom amounts separately
      const totalTips = splits.reduce((sum, split) => sum + (split.tip_amount || 0), 0);
      const totalCustom = splits.reduce((sum, split) => sum + (split.custom_amount || 0), 0);

      // Verify that the base amount covers the order total
      if (totalPaid < order.total) {
        throw new Error(`Split amounts must cover the total order amount of ${formatCurrency(order.total)}`);
      }

      // Create payment records for each split
      for (const split of splits) {
        const baseAmount = split.items.reduce((sum, item) => sum + item.amount, 0);
        const tipAmount = split.tip_amount || 0;
        const customAmount = split.custom_amount || 0;
        
        const { error } = await supabase
          .from('payments')
          .insert({
            order_id: order.id,
            amount: baseAmount,
            payment_type: split.payment_type,
            tip_amount: tipAmount,
            custom_amount: customAmount,
            status: 'completed',
            is_split: true,
            split_details: {
              items: split.items.map(item => ({
                id: item.id,
                amount: item.amount,
                quantity: item.quantity
              })),
              payment_type: split.payment_type,
              tip_amount: tipAmount,
              amount: baseAmount
            }
          });

        if (error) throw error;
      }

      // Update order status to paid
      const { error: orderError } = await supabase
        .from('orders')
        .update({ 
          status: 'paid',
          updated_at: new Date().toISOString()
        })
        .eq('id', order.id);

      if (orderError) throw orderError;

      setPaymentStatus('completed');
      addToast({
        type: 'success',
        message: `Split payment processed successfully (Total: ${formatCurrency(totalPaid + totalTips + totalCustom)})`,
      });

      setTimeout(() => {
        navigate('/orders');
      }, 2000);
    } catch (error) {
      console.error('Error processing split payment:', error);
      setPaymentStatus('error');
      addToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to process split payment',
      });
    }
  };

  if (loading) {
    return (
      <Profiler id="PaymentPage-Loading" onRender={onRender}>
        <div className="flex items-center justify-center min-h-screen-75">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-600"></div>
        </div>
      </Profiler>
    );
  }

  if (!order) {
    return (
      <Profiler id="PaymentPage-NotFound" onRender={onRender}>
        <div className="text-center py-12">
          <i className="fas fa-exclamation-circle text-4xl text-red-500 mb-4" />
          <h2 className="text-2xl font-semibold mb-2">Order Not Found</h2>
          <p className="text-gray-600 dark:text-dark-text-secondary">This order does not exist or has been deleted.</p>
        </div>
      </Profiler>
    );
  }

  return (
    <Profiler id="PaymentPage-Content" onRender={onRender}>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white dark:bg-dark-bg-light rounded-lg shadow-sm overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-border">
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-dark-text-primary">
              Payment Details
            </h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-dark-text-secondary">
              Total Amount: {formatCurrency(order.total)}
            </p>
          </div>

          {/* Payment Mode Selection */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-border">
            <h2 className="text-lg font-medium mb-3 text-gray-900 dark:text-dark-text-primary">
              Payment Mode
            </h2>
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => setPaymentMode('regular')}
                className={`flex-1 flex items-center justify-center px-4 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 ${
                  paymentMode === 'regular'
                    ? 'bg-primary-600 text-white shadow-md shadow-primary-500/20'
                    : 'bg-white dark:bg-dark-bg-light text-gray-700 dark:text-dark-text-secondary border border-gray-200 dark:border-dark-border hover:border-primary-500 dark:hover:border-primary-500 hover:text-primary-600 dark:hover:text-primary-400'
                }`}
              >
                <i className={`fas fa-user text-base mr-2 ${paymentMode === 'regular' ? 'text-white' : 'text-primary-500'}`} />
                Regular Payment
              </button>
              <button
                type="button"
                onClick={() => setPaymentMode('split')}
                className={`flex-1 flex items-center justify-center px-4 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 ${
                  paymentMode === 'split'
                    ? 'bg-primary-600 text-white shadow-md shadow-primary-500/20'
                    : 'bg-white dark:bg-dark-bg-light text-gray-700 dark:text-dark-text-secondary border border-gray-200 dark:border-dark-border hover:border-primary-500 dark:hover:border-primary-500 hover:text-primary-600 dark:hover:text-primary-400'
                }`}
              >
                <i className={`fas fa-users text-base mr-2 ${paymentMode === 'split' ? 'text-white' : 'text-primary-500'}`} />
                Split Payment
              </button>
            </div>
          </div>

          {/* Payment Method Selection */}
          {paymentMode === 'regular' && (
            <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-border">
              <h2 className="text-lg font-medium mb-3 text-gray-900 dark:text-dark-text-primary">
                Payment Method
              </h2>
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setPaymentType('cash')}
                  className={`flex-1 flex items-center justify-center px-4 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 ${
                    paymentType === 'cash'
                      ? 'bg-primary-600 text-white shadow-md shadow-primary-500/20'
                      : 'bg-white dark:bg-dark-bg-light text-gray-700 dark:text-dark-text-secondary border border-gray-200 dark:border-dark-border hover:border-primary-500 dark:hover:border-primary-500 hover:text-primary-600 dark:hover:text-primary-400'
                  }`}
                >
                  <i className={`fas fa-money-bill-wave text-base mr-2 ${paymentType === 'cash' ? 'text-white' : 'text-primary-500'}`} />
                  Cash
                </button>
                <button
                  type="button"
                  onClick={() => setPaymentType('card')}
                  className={`flex-1 flex items-center justify-center px-4 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 ${
                    paymentType === 'card'
                      ? 'bg-primary-600 text-white shadow-md shadow-primary-500/20'
                      : 'bg-white dark:bg-dark-bg-light text-gray-700 dark:text-dark-text-secondary border border-gray-200 dark:border-dark-border hover:border-primary-500 dark:hover:border-primary-500 hover:text-primary-600 dark:hover:text-primary-400'
                  }`}
                >
                  <i className={`fas fa-credit-card text-base mr-2 ${paymentType === 'card' ? 'text-white' : 'text-primary-500'}`} />
                  Card
                </button>
              </div>
            </div>
          )}

          {/* Order Summary */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-border">
            <h2 className="text-lg font-medium mb-3 text-gray-900 dark:text-dark-text-primary">
              Order Summary
            </h2>
            <div className="space-y-2">
              {order.items.map((item) => (
                <div 
                  key={item.id} 
                  className="flex items-center justify-between py-2 px-3 bg-gray-50 dark:bg-dark-bg rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-white dark:bg-dark-bg-light rounded-lg">
                      <i className={`fas fa-utensils text-base text-primary-600 dark:text-primary-400`} />
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                        {item.menu_item.name}
                      </h3>
                      <p className="text-xs text-gray-600 dark:text-dark-text-secondary">
                        {item.quantity} × {formatCurrency(item.menu_item.price)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                      {formatCurrency(item.quantity * item.menu_item.price)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Payment Form */}
          <div className="px-6 py-4">
            {paymentMode === 'regular' ? (
              <RegularPayment
                order={order}
                method={paymentType}
                status={paymentStatus}
                onSubmit={handlePaymentSubmit}
              />
            ) : (
              <SplitPayment
                order={order}
                orderItems={order.items}
                status={paymentStatus}
                onSubmit={handleSplitPayment}
              />
            )}
          </div>
        </div>
      </div>
    </Profiler>
  );
}
